// ----------------------------------------------------------------------
// 🌳 Sitemap Parser Utility
// Converts between JSON Tree structure and HTML (for RTE)
// ----------------------------------------------------------------------

// Convert Tree to HTML (for loading into RTE)
export const treeToHtml = (node) => {
  if (!node) return '';

  // 1. Process Label (Append description if exists for legacy support)
  let labelHtml = node.label || 'Untitled';
  if (node.description) {
    labelHtml += ` <span class="text-slate-400 text-sm">| ${node.description}</span>`;
  }

  // 2. Build List Item
  // Root node is treated as the first paragraph/item, children are the list
  if (node.type === 'root') {
    let childrenHtml = '';
    if (node.children && Array.isArray(node.children) && node.children.length > 0) {
      childrenHtml = `<ul>${node.children.map(child => treeToNodeHtml(child)).join('')}</ul>`;
    }
    return `<p>${labelHtml}</p>${childrenHtml}`;
  }
  
  return treeToNodeHtml(node);
};

// Helper for recursive children
const treeToNodeHtml = (node) => {
  if (!node) return '';
  
  let labelHtml = node.label || 'Untitled';
  if (node.description) {
     labelHtml += ` <span class="text-slate-400 text-sm">| ${node.description}</span>`;
  }

  let childrenHtml = '';
  if (node.children && Array.isArray(node.children) && node.children.length > 0) {
    childrenHtml = `<ul>${node.children.map(child => treeToNodeHtml(child)).join('')}</ul>`;
  }

  return `<li>${labelHtml}${childrenHtml}</li>`;
};


// ----------------------------------------------------------------------
// 🧠 Advanced HTML to Tree Parser (Linear Depth Algorithm)
// Robustly handles Quill's flat structure with ql-indent classes
// ----------------------------------------------------------------------

export const htmlToTree = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // 1. Extract Root Label
  let rootLabel = 'Home';
  let firstBlock = doc.body.firstChild;
  
  // Skip empty text nodes at start to find the first real content
  while (firstBlock && (firstBlock.nodeType === 3 && !firstBlock.textContent.trim())) {
    firstBlock = firstBlock.nextSibling;
  }

  // If the first block isn't a list, assume it's the Title/Root
  if (firstBlock && firstBlock.nodeName !== 'UL' && firstBlock.nodeName !== 'OL') {
    rootLabel = firstBlock.nodeType === 1 ? firstBlock.innerHTML : firstBlock.textContent;
    // Clean root label
    rootLabel = rootLabel.replace(/<br\s*\/?>/gi, '').trim();
  }

  const root = {
    id: 'root',
    label: rootLabel || 'Home',
    type: 'root',
    children: []
  };

  // 2. Linearize List Items
  // We grab ALL list items. Quill might nest them or keep them flat with classes.
  // We will calculate the visual "depth" of each item to reconstruct the tree.
  const listItems = Array.from(doc.querySelectorAll('li'));

  if (listItems.length === 0) return root;

  // Track the last node at each depth level to assign children correctly
  // path[0] is always root.
  // path[1] is the last node at depth 1, etc.
  const path = { 0: root };

  listItems.forEach((li) => {
    // A. Calculate Semantic Depth
    let depth = 1; // Default depth (child of root)

    // Check 1: Quill Indent Classes (ql-indent-1 = depth + 1)
    li.classList.forEach(cls => {
      if (cls.startsWith('ql-indent-')) {
        const indentVal = parseInt(cls.replace('ql-indent-', ''), 10);
        if (!isNaN(indentVal)) depth += indentVal;
      }
    });

    // Check 2: DOM Nesting (nested <ul>)
    // If we are inside nested ULs, add to depth
    let parent = li.parentElement;
    let nestingLevel = 0;
    while (parent && parent !== doc.body) {
      if (parent.tagName === 'UL' || parent.tagName === 'OL') {
        nestingLevel++;
      }
      parent = parent.parentElement;
    }
    // If nesting is present, it usually adds to the visual indentation.
    // However, Quill usually does EITHER nesting OR classes. 
    // If both exist, we might overcount, but usually standard copy-paste is nested, Quill editor is classes.
    // We'll trust the larger of the two signals or combine if safer.
    // For this specific issue, let's assume classes dictate the visual override if present.
    if (nestingLevel > 1 && !li.className.includes('ql-indent-')) {
       depth = nestingLevel;
    }

    // B. Clean Content
    // Clone to remove children lists so we just get the text/content of THIS node
    const clone = li.cloneNode(true);
    clone.querySelectorAll('ul, ol').forEach(e => e.remove());
    
    let label = clone.innerHTML.trim();
    // Remove trailing breaks and empty spans
    label = label.replace(/<br\s*\/?>$/, '');
    
    // Safety check for empty nodes
    if (!label) return; 

    // C. Create Node
    const newNode = {
      id: `node-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
      label: label,
      type: depth === 1 ? 'category' : 'page', // Depth 1 = Category, Depth > 1 = Page
      children: []
    };

    // D. Find Parent
    // The parent is the last node seen at (depth - 1).
    // If (depth - 1) doesn't exist in our path (e.g., skipped level), find the nearest existing ancestor.
    let parentDepth = depth - 1;
    while (parentDepth > 0 && !path[parentDepth]) {
      parentDepth--;
    }
    
    // Fallback to root (depth 0) if calculation fails
    const parentNode = path[parentDepth] || root;
    
    // E. Link Node
    if (!parentNode.children) parentNode.children = [];
    parentNode.children.push(newNode);

    // F. Update Path
    path[depth] = newNode;
    
    // Clear any deeper paths from previous branches to avoid incorrect parenting
    // (e.g., if we go back up from depth 3 to 1, depth 2 and 3 are no longer valid parents)
    for (let k in path) {
      if (parseInt(k) > depth) delete path[k];
    }
  });

  return root;
};